import { useState, useCallback, useRef } from "react";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";

interface EbdaaLogoProps {
  size?: "sm" | "md" | "lg";
  showText?: boolean;
  subText?: string;
  onClick?: () => void;
}

export default function EbdaaLogo({
  size = "md",
  showText = true,
  subText,
  onClick,
}: EbdaaLogoProps) {
  const tc = useThemeColor();
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [particles, setParticles] = useState<{ id: number; angle: number }[]>([]);
  const pidRef = useRef(0);

  const iconSize = size === "sm" ? 32 : size === "lg" ? 52 : 40;
  const textSize = size === "sm" ? "text-base" : size === "lg" ? "text-2xl" : "text-lg";
  const subSize = size === "sm" ? "text-[8px]" : size === "lg" ? "text-[11px]" : "text-[9px]";

  const spawnParticles = useCallback(() => {
    const ps = Array.from({ length: 8 }, (_, i) => ({
      id: pidRef.current++,
      angle: i * 45,
    }));
    setParticles(ps);
    setTimeout(() => setParticles([]), 700);
  }, []);

  const handleClick = () => {
    setClicked(true);
    spawnParticles();
    setTimeout(() => setClicked(false), 350);
    onClick?.();
  };

  const scale = clicked ? 0.9 : hovered ? 1.06 : 1;

  return (
    <div
      className="flex items-center gap-2.5 cursor-pointer select-none"
      style={{ transform: `scale(${scale})`, transition: "transform 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* ── Icon Mark ── */}
      <div
        className="relative flex-shrink-0 flex items-center justify-center"
        style={{ width: iconSize, height: iconSize }}
      >
        {/* Outer glow ring */}
        <div
          className="absolute inset-0 rounded-xl"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${hexAlpha(tc.primary, hovered ? 0.25 : 0.12)} 0%, transparent 70%)`,
            transform: hovered ? "scale(1.3)" : "scale(1)",
            transition: "transform 0.4s ease, background 0.3s ease",
          }}
        />

        {/* Main icon container */}
        <div
          className="absolute inset-0 rounded-xl"
          style={{
            background: hovered
              ? `linear-gradient(135deg, ${hexAlpha(tc.primary, 0.22)} 0%, ${hexAlpha(tc.primary, 0.08)} 100%)`
              : `linear-gradient(135deg, ${hexAlpha(tc.primary, 0.14)} 0%, ${hexAlpha(tc.primary, 0.04)} 100%)`,
            border: `1.5px solid ${hovered ? hexAlpha(tc.primary, 0.7) : hexAlpha(tc.primary, 0.35)}`,
            boxShadow: hovered
              ? `0 0 20px ${hexAlpha(tc.primary, 0.35)}, inset 0 0 12px ${hexAlpha(tc.primary, 0.08)}`
              : `0 0 8px ${hexAlpha(tc.primary, 0.1)}`,
            transition: "all 0.3s ease",
          }}
        />

        {/* Pulse ring */}
        <div
          className="absolute inset-0 rounded-xl border"
          style={{
            borderColor: hexAlpha(tc.primary, 0.3),
            animation: "ebdaa-logo-pulse 2.4s ease-in-out infinite",
          }}
        />

        {/* "E" lettermark — geometric style */}
        <div className="relative z-10 flex items-center justify-center" style={{ width: iconSize * 0.55, height: iconSize * 0.65 }}>
          {/* Top bar */}
          <div
            className="absolute top-0 left-0 right-0"
            style={{
              height: Math.max(2, iconSize * 0.065),
              background: hovered
                ? `linear-gradient(to right, ${tc.primary}, ${tc.primaryLight})`
                : tc.primary,
              borderRadius: 99,
              boxShadow: hovered ? `0 0 8px ${hexAlpha(tc.primary, 0.8)}` : "none",
              transition: "all 0.3s ease",
            }}
          />
          {/* Middle bar — shorter */}
          <div
            className="absolute"
            style={{
              top: "50%",
              left: 0,
              right: "20%",
              height: Math.max(2, iconSize * 0.065),
              transform: "translateY(-50%)",
              background: hovered
                ? `linear-gradient(to right, ${tc.primary}, ${tc.primaryLight})`
                : tc.primary,
              borderRadius: 99,
              boxShadow: hovered ? `0 0 8px ${hexAlpha(tc.primary, 0.8)}` : "none",
              transition: "all 0.3s ease",
              opacity: hovered ? 1 : 0.85,
            }}
          />
          {/* Bottom bar */}
          <div
            className="absolute bottom-0 left-0 right-0"
            style={{
              height: Math.max(2, iconSize * 0.065),
              background: hovered
                ? `linear-gradient(to right, ${tc.primary}, ${tc.primaryLight})`
                : tc.primary,
              borderRadius: 99,
              boxShadow: hovered ? `0 0 8px ${hexAlpha(tc.primary, 0.8)}` : "none",
              transition: "all 0.3s ease",
            }}
          />
          {/* Left vertical bar */}
          <div
            className="absolute top-0 bottom-0 left-0"
            style={{
              width: Math.max(2, iconSize * 0.065),
              background: hovered
                ? `linear-gradient(to bottom, ${tc.primary}, ${tc.primaryLight}, ${tc.primary})`
                : tc.primary,
              borderRadius: 99,
              boxShadow: hovered ? `0 0 8px ${hexAlpha(tc.primary, 0.8)}` : "none",
              transition: "all 0.3s ease",
            }}
          />
          {/* Accent dot — top right corner */}
          <div
            className="absolute"
            style={{
              top: -Math.max(2, iconSize * 0.08),
              right: -Math.max(2, iconSize * 0.08),
              width: Math.max(4, iconSize * 0.14),
              height: Math.max(4, iconSize * 0.14),
              borderRadius: "50%",
              background: tc.primary,
              boxShadow: `0 0 ${hovered ? 10 : 5}px ${hexAlpha(tc.primary, hovered ? 1 : 0.6)}`,
              transition: "all 0.3s ease",
              transform: hovered ? "scale(1.3)" : "scale(1)",
            }}
          />
        </div>

        {/* Spark particles */}
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 4,
              height: 4,
              background: tc.primary,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              animation: `ebdaa-spark-${p.angle} 0.65s ease-out forwards`,
            }}
          />
        ))}
      </div>

      {/* ── Text Mark ── */}
      {showText && (
        <div className="flex flex-col leading-none gap-0.5">
          {/* Main wordmark */}
          <span
            className={`font-black tracking-wider ${textSize}`}
            style={{
              fontFamily: "'Montserrat', 'Cairo', sans-serif",
              background: hovered
                ? `linear-gradient(to right, ${tc.primaryLight}, ${tc.primary}, ${tc.primaryDark})`
                : `linear-gradient(to right, ${tc.primaryLight}, ${tc.primary}, ${tc.primaryDark})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: hovered
                ? `drop-shadow(0 0 12px ${hexAlpha(tc.primary, 0.8)})`
                : `drop-shadow(0 0 6px ${hexAlpha(tc.primary, 0.3)})`,
              transition: "filter 0.3s ease",
              letterSpacing: hovered ? "0.12em" : "0.08em",
            }}
          >
            Ebdaa
          </span>

          {/* Sub text */}
          {subText && (
            <span
              className={`tracking-[0.22em] uppercase font-semibold ${subSize}`}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                color: hovered ? hexAlpha(tc.primary, 0.7) : hexAlpha(tc.primary, 0.4),
                letterSpacing: hovered ? "0.3em" : "0.22em",
                transition: "all 0.3s ease",
              }}
            >
              {subText}
            </span>
          )}
        </div>
      )}

      <style>{`
        @keyframes ebdaa-logo-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.1); }
        }
        @keyframes ebdaa-spark-0   { to { transform: translate(calc(-50% + 22px), calc(-50% - 22px)); opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-45  { to { transform: translate(calc(-50% + 24px), calc(-50%));         opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-90  { to { transform: translate(calc(-50% + 22px), calc(-50% + 22px)); opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-135 { to { transform: translate(calc(-50%),        calc(-50% + 24px)); opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-180 { to { transform: translate(calc(-50% - 22px), calc(-50% + 22px)); opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-225 { to { transform: translate(calc(-50% - 24px), calc(-50%));         opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-270 { to { transform: translate(calc(-50% - 22px), calc(-50% - 22px)); opacity: 0; width: 3px; height: 3px; } }
        @keyframes ebdaa-spark-315 { to { transform: translate(calc(-50%),        calc(-50% - 24px)); opacity: 0; width: 3px; height: 3px; } }
      `}</style>
    </div>
  );
}
